<template>
  <div id="johkumax">
    <h3>{{ _('Johku Max') }}</h3>
    <p v-if="loading && !loaded"><i class="fa fa-fw fa-spinner fa-spin"></i> {{ _('Loading...') }}</p>
    <template v-else-if="loaded">
      <div class="alert alert-danger" v-if="lastError && !pending">{{ lastError }}</div>
      <div class="alert alert-info" v-if="pending">
        <i class="fa fa-fw fa-spinner fa-spin"></i> {{ pendingText }}
      </div>

      <template v-if="isActive">
        <div class="alert alert-danger" v-if="isSuspended">{{ _('Johku Max is suspended because the latest payment is overdue. It will be restored once the payment is received.') }}</div>
        <dl class="dl-horizontal">
          <dt>{{ _('Billing term') }}</dt>
          <dd>{{ termName(subscription.term) }}</dd>
          <dt>{{ _('Extra users') }}</dt>
          <dd>
            {{ subscription.seats }}
            <span v-if="subscription.scheduledSeats !== null" class="text-muted">({{ _('%s from the next billing period').replace('%s', subscription.scheduledSeats) }})</span>
          </dd>
          <dt>{{ _('Current period') }}</dt>
          <dd>{{ formatDate(subscription.periodStart) }}–{{ formatLastDay(subscription.periodEnd) }}</dd>
          <dt>{{ _('Paid until') }}</dt>
          <dd>{{ formatLastDay(subscription.validUntil) }}</dd>
          <dt v-if="!subscription.cancelAtPeriodEnd">{{ _('Next invoice') }}</dt>
          <dd v-if="!subscription.cancelAtPeriodEnd">{{ formatDate(subscription.nextBilling) }}</dd>
        </dl>

        <h4>{{ _('Users') }}</h4>
        <p>{{ _('The license includes %s users, and you can buy more. A user gets access the first time they use the Johku admin tools with an AI assistant, if there is room. Remove a user to give their place to someone else.').replace('%s', includedSeats) }}</p>
        <p v-if="seatList">
          <strong>{{ _('%1$s of %2$s users in use').replace('%1$s', seatList.used).replace('%2$s', seatList.quota || 0) }}</strong>
        </p>
        <p v-if="seatList && !seatList.seats.length" class="text-muted">{{ _('No one has used the Johku admin tools yet.') }}</p>
        <table class="table table-condensed" v-if="seatList && seatList.seats.length">
          <thead>
            <tr>
              <th>{{ _('Name') }}</th>
              <th>{{ _('Email') }}</th>
              <th>{{ _('Last used') }}</th>
              <th><span class="sr-only">{{ _('Actions') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="seat in seatList.seats" :key="seat.id">
              <td>
                {{ seat.name }}
                <span v-if="!seat.active" class="label label-warning">{{ _('No access, over the user limit') }}</span>
              </td>
              <td>{{ seat.email }}</td>
              <td>{{ formatDateTime(seat.lastUsed) }}</td>
              <td class="text-right">
                <button class="btn btn-xs btn-default" type="button" v-on:click="removeSeatDialog(seat)" :disabled="busy"><i class="fa fa-fw fa-user-times"></i> {{ _('Remove') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="subscription.paymentUrl">
          <a class="btn btn-primary" :href="subscription.paymentUrl" target="_blank" rel="noopener"><i class="fa fa-fw fa-credit-card"></i> {{ _('Pay by card') }}</a>
        </p>

        <div class="alert alert-warning" v-if="subscription.cancelAtPeriodEnd">
          {{ _('Johku Max will end on %s and will not be renewed.').replace('%s', formatLastDay(subscription.validUntil)) }}
        </div>

        <h4>{{ _('Change the number of extra users') }}</h4>
        <p>{{ _('Added users are charged for the rest of the current period on your next monthly invoice. Removed users are taken off at the start of the next billing period; there are no refunds.') }}</p>
        <div class="form-inline">
          <div class="form-group">
            <label for="johkumaxSeats" class="sr-only">{{ _('Extra users') }}</label>
            <input type="number" class="form-control" id="johkumaxSeats" min="0" :max="maxSeats" v-model.number="newSeats" :disabled="busy" />
          </div>
          <button class="btn btn-default" type="button" v-on:click="changeSeats" :disabled="busy || !seatsChanged"><i class="fa fa-fw fa-users"></i> {{ _('Change users') }}</button>
        </div>
        <p class="help-block" v-if="seatsChanged && newSeats > subscription.seats && offer.seat">
          {{ _('About %s (VAT included) will be added to your next invoice.').replace('%s', formatPrice(seatChangeEstimate)) }}
        </p>

        <h4>{{ _('Renewal') }}</h4>
        <button v-if="!subscription.cancelAtPeriodEnd" class="btn btn-warning" type="button" v-on:click="cancelDialog" :disabled="busy"><i class="fa fa-fw fa-ban"></i> {{ _('Cancel Johku Max') }}</button>
        <button v-else class="btn btn-success" type="button" v-on:click="setCancel(false)" :disabled="busy"><i class="fa fa-fw fa-undo"></i> {{ _('Keep Johku Max') }}</button>
      </template>

      <template v-else-if="!pending">
        <p v-if="!offer || !offer.available">{{ _('Johku Max is not available to order yet.') }}</p>
        <template v-else>
          <p>{{ _('Johku Max is billed in advance. A yearly term is billed right away for 12 months; a monthly term is billed right away for the rest of this month and then on the first day of each month.') }}</p>
          <div class="form-group">
            <label>{{ _('Billing term') }}</label>
            <div class="radio" v-for="t in offer.terms" :key="t.term">
              <label>
                <input type="radio" name="johkumaxTerm" :value="t.term" v-model="term" :disabled="busy" />
                {{ termName(t.term) }}: {{ formatPrice(t.price) }} / {{ t.term === 'year' ? _('year') : _('month') }}
              </label>
            </div>
          </div>
          <div class="form-group" v-if="offer.seat">
            <label for="johkumaxOrderSeats">{{ _('Extra users') }}</label>
            <input type="number" class="form-control" id="johkumaxOrderSeats" min="0" :max="maxSeats" v-model.number="seats" :disabled="busy" />
            <p class="help-block">{{ _('The license includes %s users.').replace('%s', includedSeats) }} {{ _('%s per user per month. You can add users at any time during the term.').replace('%s', formatPrice(offer.seat.price)) }}</p>
          </div>
          <div class="form-group" v-if="offer.payments.length > 1">
            <label for="johkumaxPayment">{{ _('Payment method') }}</label>
            <select class="form-control" id="johkumaxPayment" v-model="payment" :disabled="busy">
              <option value="bill">{{ _('Invoice') }}</option>
              <option value="card">{{ _('Card payment') }}</option>
            </select>
          </div>
          <p v-if="selectedTerm">
            <strong>{{ term === 'year' ? _('Total for 12 months') : _('Total per month') }}: {{ formatPrice(orderTotal) }}</strong>
            <span class="text-muted"> ({{ _('VAT included') }})</span>
          </p>
          <button class="btn btn-success" type="button" v-on:click="orderDialog" :disabled="busy || !selectedTerm || !validSeats(seats)"><i class="fa fa-fw fa-shopping-cart"></i> {{ _('Order Johku Max') }}</button>
        </template>
      </template>
    </template>
  </div>
</template>

<script>

import axios from 'axios'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

const POLL_INTERVAL = 3000
const POLL_LIMIT = 40

export default {
  data () {
    return {
      loading: false,
      loaded: false,
      busy: false,
      offer: null,
      subscription: null,
      seatList: null,
      pending: null,
      lastError: null,
      term: 'year',
      seats: 0,
      payment: 'bill',
      newSeats: 0,
      maxSeats: 500,
      pollTimer: null,
      polls: 0
    }
  },
  mixins: [errors, formats, modals],
  computed: {
    ...mapGetters(['baseDomain', 'shopId', 'apiKey', 'timezone']),
    url () {
      return this.baseDomain + this.shopId + '/johkusubscriptions.json?apikey=' + this.apiKey
    },
    patchUrl () {
      return this.baseDomain + this.shopId + '/johkusubscriptions/max.json?apikey=' + this.apiKey
    },
    seatsUrl () {
      return this.baseDomain + this.shopId + '/johkusubscriptionseats.json?apikey=' + this.apiKey
    },
    includedSeats () {
      return this.offer && this.offer.includedSeats ? this.offer.includedSeats : 0
    },
    isActive () {
      return this.subscription && (this.subscription.status === 'active' || this.isSuspended)
    },
    isSuspended () {
      return this.subscription && this.subscription.status === 'suspended'
    },
    selectedTerm () {
      return this.offer && this.offer.terms ? this.offer.terms.find(t => t.term === this.term) : null
    },
    orderTotal () {
      if (!this.selectedTerm) {
        return 0
      }
      const months = this.term === 'year' ? 12 : 1
      const seatPrice = this.offer.seat ? this.offer.seat.price : 0
      return this.selectedTerm.price + (Number(this.seats) || 0) * seatPrice * months
    },
    seatsChanged () {
      return this.isActive && this.validSeats(this.newSeats) && this.newSeats !== this.currentTargetSeats
    },
    currentTargetSeats () {
      if (!this.subscription) {
        return 0
      }
      return this.subscription.scheduledSeats !== null ? this.subscription.scheduledSeats : this.subscription.seats
    },
    seatChangeEstimate () {
      if (!this.offer || !this.offer.seat || !this.subscription) {
        return 0
      }
      const now = DateTime.now().toSeconds()
      const start = this.subscription.periodStart
      const end = this.subscription.periodEnd
      const share = end > start ? Math.max(0, Math.min(1, (end - now) / (end - start))) : 0
      const months = this.subscription.term === 'year' ? 12 : 1
      return (this.newSeats - this.subscription.seats) * this.offer.seat.price * months * share
    },
    pendingText () {
      if (!this.pending) {
        return ''
      }
      switch (this.pending.type) {
        case 'order':
          return this._('Your order is being processed. The invoice will be sent to your billing email.')
        case 'seats':
          return this._('The number of users is being updated.')
        default:
          return this._('Your change is being processed.')
      }
    }
  },
  methods: {
    validSeats (n) {
      return Number.isInteger(n) && n >= 0 && n <= this.maxSeats
    },
    termName (term) {
      return term === 'year' ? this._('Yearly') : this._('Monthly')
    },
    formatPrice (amount) {
      return this.formatNumber(amount, 'fi-FI') + ' €'
    },
    formatDate (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).toFormat('d.M.yyyy')
    },
    formatLastDay (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).minus({ days: 1 }).toFormat('d.M.yyyy')
    },
    async load () {
      this.loading = true
      let response
      try {
        response = await axios.get(this.url)
      } catch (e) {
        this.loading = false
        this.showError(e)
        return
      }
      this.loading = false
      this.loaded = true
      this.offer = response.data.offer
      this.subscription = response.data.subscription
      this.pending = response.data.pending
      this.lastError = response.data.lastError
      if (this.offer && this.offer.payments && this.offer.payments.indexOf(this.payment) === -1) {
        this.payment = 'bill'
      }
      if (this.subscription) {
        this.newSeats = this.subscription.scheduledSeats !== null ? this.subscription.scheduledSeats : this.subscription.seats
      }
      if (this.isActive) {
        await this.loadSeats()
      }
      this.schedulePoll()
    },
    async loadSeats () {
      try {
        const response = await axios.get(this.seatsUrl)
        this.seatList = response.data
      } catch (e) {
        this.showError(e)
      }
    },
    formatDateTime (ts) {
      if (!ts) {
        return ''
      }
      return DateTime.fromSeconds(Number(ts), { zone: this.timezone }).toFormat('d.M.yyyy HH:mm')
    },
    removeSeatDialog (seat) {
      this.showDialog({
        title: this._('Remove user'),
        text: this._('%s will lose access to the Johku admin tools within a minute. If they use them again later, they get a place back if there is room.').replace('%s', seat.name || seat.email),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Remove'),
            class: 'danger',
            icon: 'user-times',
            handler: () => {
              this.removeSeat(seat)
            }
          }
        ]
      })
    },
    async removeSeat (seat) {
      this.busy = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/johkusubscriptionseats/' + seat.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showError(e)
      }
      this.busy = false
      await this.loadSeats()
    },
    schedulePoll () {
      clearTimeout(this.pollTimer)
      if (!this.pending) {
        this.polls = 0
        return
      }
      if (this.polls++ >= POLL_LIMIT) {
        return
      }
      this.pollTimer = setTimeout(() => this.load(), POLL_INTERVAL)
    },
    async send (method, url, data) {
      this.busy = true
      try {
        await axios[method](url, data)
      } catch (e) {
        this.busy = false
        this.showError(e)
        return false
      }
      this.busy = false
      this.polls = 0
      await this.load()
      return true
    },
    orderDialog () {
      const text = this.term === 'year'
        ? this._('You will be invoiced %s now for the next 12 months.').replace('%s', this.formatPrice(this.orderTotal))
        : this._('You will be invoiced now for the rest of this month, and %s on the first day of each month after that.').replace('%s', this.formatPrice(this.orderTotal))
      this.showDialog({
        title: this._('Order Johku Max'),
        text,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Order'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.send('post', this.url, { term: this.term, seats: this.seats, payment: this.payment })
            }
          }
        ]
      })
    },
    changeSeats () {
      if (!this.seatsChanged) {
        return
      }
      this.send('patch', this.patchUrl, { seats: this.newSeats })
    },
    cancelDialog () {
      this.showDialog({
        title: this._('Cancel Johku Max'),
        text: this._('Johku Max stays in use until %s and will not be renewed after that. You can undo this until then.').replace('%s', this.formatLastDay(this.subscription.validUntil)),
        buttons: [
          {
            title: this._('Back'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Cancel Johku Max'),
            class: 'warning',
            icon: 'ban',
            handler: () => {
              this.setCancel(true)
            }
          }
        ]
      })
    },
    setCancel (cancel) {
      this.send('patch', this.patchUrl, { cancel })
    }
  },
  mounted () {
    this.load()
  },
  beforeUnmount () {
    clearTimeout(this.pollTimer)
  }
}
</script>
